<template>
  <div>
    <b-alert v-if="localErr" show variant="danger">
      <template v-if="localErr.message">{{ localErr.message }}</template>
      <template v-else>{{ `${localErr}` }}</template>
    </b-alert>
    <b-alert v-else-if="remoteErr" show variant="danger">
      <template v-if="remoteErr.message">{{ remoteErr.message }}</template>
      <template v-else>{{ `${remoteErr}` }}</template>
    </b-alert>

    <div class="d-flex flex-row align-items-start">
      <div class="d-flex flex-column flex-fill">
        <h4>General Settings</h4>
        <p>Set the general settings for the leaderboard</p>
      </div>
      <b-button @click="() => visible.general = !visible.general" size="sm" variant="outline">
        <template v-if="visible.general">Close</template>
        <template v-else>Expand</template>
      </b-button>
    </div>
    <transition name="fade">
      <div class="d-flex flex-row align-items-start" v-if="visible.general">
        <div class="d-flex flex-column flex-fill">
          <b-form-group label-cols="12" label-cols-md="3" label="Name (Required)" label-for="name">
              <b-form-input id="name" v-model="general.name"/>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Header Description" label-for="name"
            description="This (optional) text will appear towards the top of the leaderboard page">
              <b-form-textarea id="textarea" v-model="general.description" placeholder="Enter something..." rows="3" max-rows="6"/>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Raffle Mode" label-for="raffleMode"
            description="Points become tickets, placements become random, anything goes!">
              <b-form-checkbox v-model="general.raffleMode"/>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Hide Leaderboard" label-for="raffleMode"
            description="Hide leaderboard from employees">
              <b-form-checkbox v-model="general.hidden"/>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Start Time" label-for="name"
            description="What time to do you want to start recording points?">
              <date-picker v-model="general.startDate" type="datetime" lang="en" format="YYYY-MM-DD hh:mm:ss" confirm />
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="End Time" label-for="name"
            description="What time to want the leaderboard to finish?">
              <date-picker v-model="general.endDate" type="datetime" lang="en" format="YYYY-MM-DD hh:mm:ss" confirm />
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Status" label-for="status">
            <b-form-select id="status" :options="statusOptions" v-model="general.status">
              <template v-slot:first><option :value="null" disabled>-- Please select an option --</option></template>
            </b-form-select>
          </b-form-group>
        </div>
      </div>
    </transition>

    <hr>

    <div class="d-flex flex-row align-items-start">
      <div class="d-flex flex-column flex-fill">
        <h4>Membership</h4>
        <p>Choose which employees to include in this leaderboard &amp; how they should compete against each other</p>
      </div>
      <b-button @click="() => visible.membership = !visible.membership" size="sm" variant="outline">
        <template v-if="visible.membership">Close</template>
        <template v-else>Expand</template>
      </b-button>
    </div>
    <transition name="fade">
      <div class="d-flex flex-column" v-if="visible.membership">
        <b-form-group label-cols="12" label-cols-md="3" label="Group By">
          <b-form-radio-group class="pt-1" stacked v-model="groupBy">
            <b-form-radio :value="null" default>Each user appears individually</b-form-radio>
            <b-form-radio value="SEGMENTS">Group users by their segment</b-form-radio>
          </b-form-radio-group>
        </b-form-group>
        <b-form-group label-cols="12" label-cols-md="3" label="Segments">
          <b-form-checkbox-group :options="segmentsList" stacked v-model="segmentIDs"/>
        </b-form-group>
        <b-alert v-if="isSuperAdmin" class="px-1" show variant="danger">
          <b-form-group label-cols="12" label-cols-md="3" label="Include All Users (Deprecated)">
            <p><strong>Hey Admin,</strong> this section will be removed soon!<br/>
              Please use the <strong>Segments</strong> &amp; <strong>Group By</strong> options instead!</p>
            <toggle class="m-0" style="width: 200px;" v-model="membership.everyone" label-true="Yes" label-false="No"/>
            <template v-if="!membership.everyone">
              <b-spinner v-if="$apollo.queries.potentialMembership.loading" type="grow" variant="primary"/>
              <template v-else-if="potentialMembership">
                <hr>
                <b-form-group label="Select Departments to include in the leaderboard" v-if="Array.isArray(membership.departmentIDs)">
                  <b-form-checkbox v-for="department in potentialMembership.departments" :key="department.id" :checked="membership.departmentIDs.includes(department.id)" inline @input="value => checkDepartment(department.id, value) ">
                    <font-awesome-icon :icon="department.icon" /> {{ department.name }}
                  </b-form-checkbox>
                </b-form-group>
                <hr>
                <b-form-group label="Select users to include in the leaderboard">
                  <multiselect v-model="membership.userIDs" :options="potentialMembership.users"
                    placeholder="Select one or leave blank" label="fullName" track-by="id" class="multiselect-rlnx"
                    multiple taggable tag-position="bottom">
                      <template v-slot:noResult>Not found - has the employee signed up to the platform yet?</template>
                  </multiselect>
                </b-form-group>
              </template>
            </template>
          </b-form-group>
        </b-alert>
      </div>
    </transition>

    <hr>

    <div class="d-flex flex-row align-items-start">
      <div class="d-flex flex-column flex-fill">
        <h4>Scores</h4>
        <p>Define the score system for this leaderboard</p>
      </div>
      <b-button @click="() => visible.scores = !visible.scores" size="sm" variant="outline">
        <template v-if="visible.scores">Close</template>
        <template v-else>Expand</template>
      </b-button>
    </div>

    <div class="d-flex flex-column flex-fill" v-if="visible.scores">
      <b-card class="mb-4" v-for="(score, i) in scores" :key="`${i}-${score.id}`">
        <b-form-group label-cols="12" label-cols-md="3" label="Label (Required)" description="This name appears on the Leaderboard column header">
          <b-form-input v-model="score.label"/>
        </b-form-group>
        <b-form-group label-cols="12" label-cols-md="3" label="Value (Required)"
          :description="general.raffleMode ? 'In raffle mode, these are the number of tickets awarded'
            : 'In score mode, these are the number of points awarded'">
          <b-form-input required type="number" @mousewheel="e => e.preventDefault()" v-model="score.value"/>
        </b-form-group>
        <!-- <b-form-group label-cols="12" label-cols-md="3" label="Maximum" description="Enforce a limit of events on a leaderboard">
          <b-form-input placeholder="(None)" type="number" @mousewheel="e => e.preventDefault()" v-model="score.maximum"/>
        </b-form-group> -->
        <b-form-group label-cols="12" label-cols-md="3" label="Description" description="This appears in the explainations of the score system">
          <b-form-input v-model="score.description"/>
        </b-form-group>
        <b-form-group label-cols="12" label-cols-md="3" label="Icon" description="This appears in the explainations of the score system">
          <b-form-radio v-for="icon in scoreIcons" :key="`${i}-${icon}`" :value="icon" v-model="score.icon" :name="`${score.id}-icon`">
            <font-awesome-icon :icon="icon" />
          </b-form-radio>
        </b-form-group>
        <b-form-group label-cols="12" label-cols-md="3" label="Event Names">
          <checkbox-group :name="`${score.id}-scores`" :options="scoreEventNames" v-model="score.eventNames"/>
        </b-form-group>
        <b-form-group label-cols="12" label-cols-md="3" label="Disabled"
          description="Mark this as disabled to hide from the leaderboard and not collect any points for the events">
            <b-form-checkbox v-model="score.disabled"/>
        </b-form-group>
        <div class="d-flex flex-row justify-content-end">
          <b-btn variant="outline-primary mr-2" @click="() => scoreMoveItem(true, i)">Up</b-btn>
          <b-btn variant="outline-primary mr-2" @click="() => scoreMoveItem(false, i)">Down</b-btn>
          <b-btn variant="outline-danger" @click="() => scores.splice(i, 1)">Remove</b-btn>
        </div>
      </b-card>
      <div class="d-flex flex-row justify-content-end">
        <b-btn variant="outline-primary" @click="() => scores.push({ id: Date.now(), eventNames: [], value: 1 })">Add New</b-btn>
      </div>
    </div>

    <hr/>

    <div class="d-flex flex-row align-items-start">
      <div class="d-flex flex-column flex-fill">
        <h4>Prizes</h4>
        <p>Determine the prizes for this leaderboard</p>
      </div>
      <b-button @click="() => visible.prizes = !visible.prizes" size="sm" variant="outline">
        <template v-if="visible.prizes">Close</template>
        <template v-else>Expand</template>
      </b-button>
    </div>

    <div class="d-flex flex-row align-items-start" v-if="visible.prizes">
      <div class="d-flex flex-column flex-fill">
        <b-form-group label="Prizes" description="Which prizes do you want to award?">
          <b-form-checkbox v-model="visible.firstPlace">First Place</b-form-checkbox>
          <b-form-checkbox v-model="visible.secondPlace">Second Place</b-form-checkbox>
          <b-form-checkbox v-model="visible.thirdPlace">Third Place</b-form-checkbox>
          <b-form-checkbox v-model="visible.prizeText">Prize Description</b-form-checkbox>
        </b-form-group>
        <div v-if="visible.firstPlace">
          <h4 class="grey border-bottom pb-2">🥇 First Place Prize</h4>
          <b-form-group label-cols="12" label-cols-md="3" label="Title">
            <b-form-input v-model="prizes.first.title"></b-form-input>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Description">
            <b-form-textarea v-model="prizes.first.description" rows="2" max-rows="4"/>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Image">
            <asset-block :field="{ type: 'IMAGE' }" v-model="prizes.first.image"/>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Hidden"
            description="You can optionally hide the prize slot from the leaderboard">
              <b-form-checkbox v-model="prizes.first.hidden"/>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Terms">
            <b-form-textarea v-model="prizes.first.terms" rows="2" max-rows="4"/>
          </b-form-group>
        </div>
        <div v-if="visible.secondPlace">
          <h4 class="grey border-bottom pb-2">🥈 Second Place Prize</h4>
          <b-form-group label-cols="12" label-cols-md="3" label="Title">
            <b-form-input v-model="prizes.second.title"></b-form-input>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Description">
            <b-form-textarea v-model="prizes.second.description" rows="2" max-rows="4"/>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Image">
            <asset-block :field="{ type: 'IMAGE' }" v-model="prizes.second.image"/>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Hidden"
            description="You can optionally hide the prize slot from the leaderboard">
              <b-form-checkbox v-model="prizes.second.hidden"/>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Terms">
            <b-form-textarea v-model="prizes.second.terms" rows="2" max-rows="4"/>
          </b-form-group>
        </div>
        <div v-if="visible.thirdPlace">
          <h4 class="grey border-bottom pb-2">🥉 Third Place Prize</h4>
          <b-form-group label-cols="12" label-cols-md="3" label="Title">
            <b-form-input v-model="prizes.third.title"></b-form-input>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Description">
            <b-form-textarea v-model="prizes.third.description" rows="2" max-rows="4"/>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Image">
            <asset-block :field="{ type: 'IMAGE' }" v-model="prizes.third.image"/>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Hidden"
            description="You can optionally hide the prize slot from the leaderboard">
              <b-form-checkbox v-model="prizes.third.hidden"/>
          </b-form-group>
          <b-form-group label-cols="12" label-cols-md="3" label="Terms">
            <b-form-textarea v-model="prizes.third.terms" rows="2" max-rows="4"/>
          </b-form-group>
        </div>
        <template v-if="visible.prizeText">
          <h4 class="grey border-bottom pb-2">📝 Prize Description</h4>
          <b-form-group>
            <b-form-textarea v-model="prizes.text" placeholder="Enter something..." rows="3" max-rows="6"/>
          </b-form-group>
        </template>
      </div>
    </div>

    <template v-if="general.status !== 'ACTIVE'">
      <hr/>
      <b-alert show variant="warning">
        Since this leaderboard status is <strong>not set to Active</strong>, the score system will be deactivated for
        this leaderboard and no points will be collected.
      </b-alert>
    </template>

    <div class="d-flex justify-content-end align-items-center border-top pt-3">
      <b-button variant="success" @click="saveLeaderboard">{{ isCreate ? 'Create' : 'Update' }}</b-button>
    </div>
  </div>
</template>

<script>
import _pick from 'lodash/pick';
import assert from 'http-assert-plus';
import gql from 'graphql-tag';
import AssetBlock from '@/components/settings/AssetBlock';
import Toggle from '@/components/misc/toggle';
import IconPicker from '@/components/misc/iconPicker';
import CheckboxGroup from '@/components/settings/CheckboxGroup';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import DatePicker from 'vue2-datepicker';
import Multiselect from 'vue-multiselect';
import { mapRoot } from '@src/mixins/root';

const defaultSegment = { id: 'ALL-ACTIVE-EMPLOYEES', title: 'All Active Employees' };

export default {
  props: {
    remoteErr: {
      type: [ String, Object, Error ],
    },
    segments: {
      type: Array,
      default: () => ([]),
    },
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    AssetBlock,
    CheckboxGroup,
    Toggle,
    IconPicker,
    DatePicker,
    LoadingSpinner,
    Multiselect,
  },
  data: () => ({
    localErr: null,
    potentialMembershipErr: null,

    general: {},
    membership: { // @TODO To remove
      type: 'INDIVIDUALS',
      everyone: true,
      departmentIDs: [],
      userIDs: [],
    },
    segmentIDs: [],
    groupBy: null,
    scores: [],
    prizes: {
      first: {},
      second: {},
      third: {},
      text: null,
    },

    visible: {
      general: false,
      membership: false,
      scores: false,
      prizes: false,
      firstPlace: false,
      secondPlace: false,
      thirdPlace: false,
      prizeText: false,
    },
  }),
  created() {
    if (this.value && this.value.id) {
      const { membership, segments, prizes } = this.value;
      this.general = {
        name: this.value.name,
        description: this.value.description,
        startDate: this.value.startDate,
        endDate: this.value.endDate,
        raffleMode: this.value.raffleMode,
        hidden: this.value.hidden,
        status: this.value.status,
      };

      this.scores = this.value.scores.map(({ __typename, ...score }) => ({ ...score }));
      this.segmentIDs = (Array.isArray(segments) && segments.length) ? segments.map(({ id }) => id) : [ defaultSegment.id ];
      this.groupBy = this.value.groupBy;

      this.membership = { // @TODO To remove & cleanup
        type: this.value.type,
        everyone: membership.hasOwnProperty('everyone') ? membership.everyone : this.membership.everyone,
        departmentIDs: (membership.departments || []).map(({ id }) => id),
        userIDs: membership.users || [],
      };

      (({ text, ...prizes }) => {
        [ 'first', 'second', 'third' ].forEach(level => {
          if (prizes[level]) {
            const { title, description, image, url } = prizes[level];
            this.prizes[level] = { title, description, image, url };
            this.visible[`${level}Place`] = true;
          }
        });
        if (text) {
          this.prizes.text = text;
          this.visible.prizeText = true;
        }
      })(prizes || {});
    } else {
      this.general.status = 'INACTIVE';
      this.visible.general = true;
    }
  },
  apollo: {
    potentialMembership: {
      query: gql`
        query LeaderboardAllUsersList {
          company {
            id
            departments(where: { status: ACTIVE }) {
              id
              name
              icon
            }
            users(where: { status: ACTIVE }) {
              id
              fullName
            }
          }
        }
      `,
      update: ({ company: { departments, users } }) => ({ departments, users }),
      error(err) {
        this.$root.onError(err);
        this.$notifyErr(err);
      },
      skip() {
        return this.membership.everyone;
      },
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'isSuperAdmin' ]),
    isCreate() {
      return !this.value || !this.value.id;
    },
    isRaffleMode() {
      const { raffleMode } = this.general || {};
      return raffleMode === true;
    },
    statusOptions: () => ([
      { text: 'Active', value: 'ACTIVE' },
      { text: 'Inactive', value: 'INACTIVE' },
      { text: 'Deleted', value: 'DELETED' },
    ]),
    scoreIcons: () => ([
      'plug',
      'link',
      'file-alt',
      'users',
      'handshake',
    ]),
    segmentsList() {
      const segments = (Array.isArray(this.segments) && this.segments.length) ? this.segments : [ defaultSegment ];
      const onlySelectedID = this.segmentIDs.length === 1 ? this.segmentIDs[0] : null;
      return segments.map(({ id, title }) => ({ text: title, value: id, disabled: id === onlySelectedID }));
    },
    scoreEventNames: () => ([
      {
        text: 'User Signed Up',
        value: 'USER_SIGNUP_SUCCESS',
        tooltip: 'Awarded when a user signs-up',
      },
      {
        text: 'Referred User Signed Up',
        value: 'USER_SIGNUP_REFERRER',
        tooltip: 'Awarded when a user\'s referred user signs-up',
      },
      {
        text: 'Contact Network Sync',
        value: 'CONTACT_NETWORK_SYNCED',
        tooltip: 'Awarded when a user syncs a network (each time)',
      },
      {
        text: 'Referred User Network Sync',
        value: 'CONTACT_NETWORK_REFERRER_SYNCED',
        tooltip: 'Awarded when a user\'s referred user syncs a network (each time)',
      },
      {
        text: 'Individual Contact Uploaded',
        value: 'CONTACT_UPLOADED',
        tooltip: 'Awarded when a user uploads a contact (each time)',
      },
      {
        text: 'Job Shared',
        value: 'JOB_SHARED',
        tooltip: 'Awarded when a user shares a job (each time)',
      },
      {
        text: 'Job Viewed',
        value: 'SHARE_VISITED',
        tooltip: 'Awarded when a visitor views a share link (each time)',
      },
      {
        text: 'Job Viewed (Unique)',
        value: 'SHARE_UNIQUE_VISITED',
        tooltip: 'Awarded when a unique visitor views a share link',
      },
      {
        text: 'Content Shared',
        value: 'CONTENT_SHARED',
        tooltip: 'Awarded when a user shares a content link (each time)',
      },
      {
        text: 'Content Viewed',
        value: 'CONTENT_SHARE_VISITED',
        tooltip: 'Awarded when a visitor views content (each time)',
      },
      {
        text: 'Referral Suggested',
        value: 'REFERRAL_SUGGESTED',
        tooltip: 'Awarded when a user chooses to refer a contact',
      },
      // {
      //   text: 'Referral Requested',
      //   value: 'REFERRAL_REQUESTED',
      //   tooltip: 'Awarded when a user is sent a referral request',
      // },
      {
        text: 'Referral Accepted',
        value: 'REFERRAL_ACCEPTED',
        tooltip: 'Awarded when a user accepts a referral request',
      },
      {
        text: 'Referral Viewed',
        value: 'REFERRAL_VISITED',
        tooltip: 'Awarded when a visitor views a referral links (each time)',
      },
      {
        text: 'Referral Viewed (Unique)',
        value: 'REFERRAL_UNIQUE_VISITED',
        tooltip: 'Awarded when a visitor views a referral links (once)',
      },
      {
        text: 'Application Created',
        value: 'APPLICATION_CREATED',
        tooltip: 'Awarded when a user\'s candidate applies for a role',
      },
      {
        text: 'Application Confirmed',
        value: 'APPLICATION_CONFIRMED',
        tooltip: 'Awarded when a user\'s candidate application is confirmed (this is only used when the application is submitted in your ATS after a redirect from Real Links.)',
      },
      {
        text: 'Application Interviewed',
        value: 'APPLICATION_INTERVIEW',
        tooltip: 'Awarded when a user\'s candidate is moved to Interview',
      },
      {
        text: 'Application Hired',
        value: 'APPLICATION_HIRED',
        tooltip: 'Awarded when a user\'s candidate is moved to Hired',
      },
      {
        text: 'Application Rejected',
        value: 'APPLICATION_REJECTED',
        tooltip: 'Awarded when a user\'s candidate application is rejected',
      },
    ]),
  },
  methods: {
    checkDepartment(departmentID, value) {
      if (!Array.isArray(this.membership.departmentIDs)) {
        this.membership.departmentIDs = [];
      }
      if (value === true && !this.membership.departmentIDs.includes(departmentID)) {
        this.membership.departmentIDs.push(departmentID);
      }
      if (value === false && this.membership.departmentIDs.includes(departmentID)) {
        this.membership.departmentIDs.splice(this.membership.departmentIDs.indexOf(departmentID), 1);
      }
    },
    scoreMoveItem(up, i) {
      if (i === 0 && up) {
        return false;
      }
      if (i === this.scores.length - 1 && !up) {
        return false;
      }

      const j = up ? i - 1 : i + 1;
      const [ value ] = this.scores.splice(i, 1);
      this.scores.splice(j, 0, value);
    },
    saveLeaderboard() {
      try {
        const payload = pickProperties({
          ...this.general,
          membership: { // @TODO To remove
            type: this.membership.type,
            ...(this.membership.everyone === true ? { everyone: true } : {}),
            ...(Array.isArray(this.membership.departmentIDs) && this.membership.departmentIDs.length
              ? { departments: this.membership.departmentIDs.map(id => ({ id })) } : {}),
            ...(Array.isArray(this.membership.userIDs) && this.membership.userIDs.length
              ? { users: this.membership.userIDs.map(({ id }) => ({ id })) } : {}),
          },
          segments: this.segmentIDs.map(id => ({ id })),
          groupBy: this.groupBy,
          prizes: this.prizes,
          scores: this.scores,
        });
        this.$emit('input', payload);
      } catch (err) {
        this.$notifyErr(err, { prepend: `Failed to ${this.isCreate ? 'create' : 'update'} leaderboard:` });
        this.$log.error(err);
        this.localErr = err;
      }
    },
  }
};

export function pickProperties(input) {
  assert(input && typeof input.name === 'string' && input.name.length, 'Missing name for leaderboard');
  const { scores, membership, groupBy, prizes, ...general } = input;

  const output = {
    ..._pick(general, [ 'name', 'description', 'startDate', 'endDate', 'raffleMode', 'hidden', 'status' ]),
    groupBy,
    scores: (Array.isArray(scores) ? scores : []).map(({ __typename, err, label, value, maximum, ...score }) => ({
      ...score,
      label: label || 'Event',
      value: value ? parseInt(value, 10) : 1,
      maximum: maximum ? parseInt(maximum, 10) : null,
    })),
  };

  (({ type, everyone, departments, users }) => { // @TODO To remove & cleanup
    output.type = type;

    const validArr = a => Array.isArray(a) && a.length;

    // If membership.everyone then delete membership.departments
    if (!everyone && (validArr(departments) || validArr(users))) {
      output.membership = { everyone: false };
      if (validArr(departments)) {
        output.membership.departments = { id_in: departments.map(({ id }) => id) };
      }
      if (validArr(users)) {
        output.membership.users = { id_in: users.map(({ id }) => id) };
      }
    } else {
      output.membership = { everyone: true };
    }
  })(membership);

  output.segments = (Array.isArray(input.segments) && input.segments.length)
    ? { id_in: input.segments.map(({ id }) => id) }
    : { id: 'ALL-ACTIVE-EMPLOYEES' };

  (({ first, second, third, text }) => {
    const collection = {};

    if (first && first.title) {
      const { __typename, image, ...rest } = first;
      collection.first = { ...rest, image: image ? { id: image.id } : null };
    }
    if (second && second.title) {
      const { __typename, image, ...rest } = second;
      collection.second = { ...rest, image: image ? { id: image.id } : null };
    }
    if (third && third.title) {
      const { __typename, image, ...rest } = third;
      collection.third = { ...rest, image: image ? { id: image.id } : null };
    }
    if (text) {
      collection.text = text;
    }

    output.prizes = Object.keys(collection).length ? collection : null;
  })(prizes);

  return output;
}
</script>



// WEBPACK FOOTER //
// src/components/settings/LeaderboardModal.vue